import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { confirmSignIn } from "aws-amplify/auth";
import {
  Button,
  Stack,
  Text,
  LoadingOverlay,
  Box,
  Group,
  Title,
  Center,
  Loader,
} from "@mantine/core";
import PasswordRequirementInput from "./common/PasswordRequirementInput";
import { isPasswordValid } from "../utils/passwordValidation";
import { calculateRoutingDestination } from "../utils/routingUtils";
import styles from "./shared/AuthLayout.module.css";

function ChangeTemporaryPassword() {
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [routingMessage, setRoutingMessage] = useState("");
  const [error, setError] = useState({});
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError({});
  };

  const validatePasswords = () => {
    if (!isPasswordValid(formData.newPassword)) {
      setError({ newPassword: "Password is missing required characters" });
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError({ confirmPassword: "Passwords do not match" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswords()) return;

    setIsLoading(true);
    try {
      const signInResponse = await confirmSignIn({
        challengeResponse: formData.newPassword,
      });

      if (signInResponse.isSignedIn) {
        // Password changed successfully - now calculate where to route
        setIsLoading(false);
        setIsCalculatingRoute(true);
        setRoutingMessage("Setting up your workspace...");

        try {
          const routingResult = await calculateRoutingDestination();

          // Use window.location.href for full page reload to ensure contexts are reset
          window.location.href = routingResult.destination;
        } catch (routingError) {
          console.error("Error calculating route:", routingError);
          // Fallback to calendar on error
          window.location.href = "/admin/calendar";
        }
      } else {
        throw new Error("Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setError({ top: error.message || "Failed to change password" });
      setIsLoading(false);
      setIsCalculatingRoute(false);
    }
  };

  // Show full-screen spinner while calculating routing destination
  if (isCalculatingRoute) {
    return (
      <Box
        className={styles.pageWrapper}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          backgroundColor: "#fff",
        }}
      >
        <Center style={{ flexDirection: "column", gap: "20px" }}>
          <Loader size="lg" type="dots" color="teal" />
          <Text size="lg" fw={500} c="dimmed">
            {routingMessage}
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box className={styles.pageWrapper}>
      {isLoading && (
        <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />
      )}

      {/* Header with Logo */}
      <header className={styles.header}>
        <img
          src="/images/pagoda-logo-horizontal.png"
          alt="Pagoda"
          className={styles.logo}
        />
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Title Section */}
        <Stack gap="xs" className={styles.titleSection}>
          <Title order={1} className={styles.title}>
            Create a new password
          </Title>
          <Text size="sm" c="dimmed" className={styles.subtitle}>
            Your temporary password needs to be changed
          </Text>
        </Stack>

        {/* Form Card */}
        <Box className={styles.formCard}>
          {error.top && (
            <Text className={styles.errorMessage}>{error.top}</Text>
          )}

          <form onSubmit={handleSubmit}>
            <Stack className={styles.formStack}>
              <PasswordRequirementInput
                label="New Password"
                value={formData.newPassword}
                onChange={(e) =>
                  handleInputChange("newPassword", e.target.value)
                }
                placeholder="Enter new password"
                radius="sm"
                size="md"
                required
                showRequirements={true}
                error={error.newPassword}
              />

              <PasswordRequirementInput
                label="Confirm Password"
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange("confirmPassword", e.target.value)
                }
                placeholder="Confirm new password"
                radius="sm"
                size="md"
                required
                showRequirements={false}
                error={error.confirmPassword}
              />

              <Button
                type="submit"
                loading={isLoading}
                fullWidth
                radius="sm"
                size="md"
                className={styles.submitButton}
              >
                Change Password
              </Button>
            </Stack>
          </form>
        </Box>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <Text size="sm" className={styles.copyright}>
          © Copyright {new Date().getFullYear()}, Pagoda Labs, Inc.
        </Text>
        <Group gap="md" className={styles.footerLinks}>
          <a
            href="https://pagoda.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Privacy Policy
          </a>
          <a
            href="https://pagoda.co/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Terms & Conditions
          </a>
        </Group>
      </footer>
    </Box>
  );
}

export default ChangeTemporaryPassword;
